import { Banner, Button, Divider, Filter } from '@vrplatform/components';
import { useTeam } from '@vrplatform/data';
import {
  type MRT_ColumnDef,
  MantineReactTable,
  useLazyTable,
} from '@vrplatform/table';
import type { SelectItem } from '@vrplatform/components';
import { day, formatCurrency } from '@vrplatform/utils';
import { Group, Text } from '@mantine/core';
import { type ComponentProps, useMemo, useState } from 'react';
import { useEmbedViewMode } from '../../providers';
import { TrustReconcilationTableFilter } from './TrustReconcilationTableFilter';
import { useTrustReconcilationTableFilter } from './useTrustReconcilationTableFilter';
import {
  type TrustReconcilationTableQueryRow,
  type TrustReconcilationTableQuerySorting,
  useTrustReconcilationTableQuery,
} from './useTrustReconcilationTableQuery';

export type TrustReconcilationJournalEntriesArgs = {
  accountIds: string;
  date: string;
  party?: 'manager';
};

function useSorting() {
  const [sorting, setSorting] =
    useState<TrustReconcilationTableQuerySorting | null>(null);

  return {
    sorting,
    setSorting,
  };
}

export const TrustReconcilationTable = ({
  onContactSupport,
  onOpenJournalEntries,
}: {
  onContactSupport?: () => void;
  onOpenJournalEntries?: (args: TrustReconcilationJournalEntriesArgs) => void;
}) => {
  const { isEmbed } = useEmbedViewMode();
  const { sorting, setSorting } = useSorting();
  const [{ currency, effectiveStatementStartAt: statementStartAt }] = useTeam();
  const { filter } = useTrustReconcilationTableFilter();

  const queryData = useTrustReconcilationTableQuery(sorting || 'name');
  const rows = queryData.data?.rows ?? [];

  const columns = useMemo<MRT_ColumnDef<TrustReconcilationTableQueryRow>[]>(
    () => [
      {
        header: 'Name',
        accessorKey: 'name',
        grow: true,
        mantineTableBodyCellProps: (props) => ({
          sx: () => ({
            fontWeight:
              props.row.depth === 0
                ? 'bold'
                : props.row.depth === 1
                  ? 500
                  : 'normal',
          }),
        }),
      },
      {
        header: 'Amount',
        grow: false,
        maxSize: 150,
        mantineTableHeadCellProps: {
          align: 'right',
        },
        mantineTableBodyCellProps: (props) => ({
          align: 'right',
          sx: () => ({
            fontWeight: props.row.depth !== 2 ? 'bold' : 'normal',
          }),
        }),
        accessorFn: (row) => formatCurrency(row.centTotal / 100, currency),
        Cell: ({ row, cell }) => {
          const accountId = row.original.id;
          const isManager =
            row.getParentRow()?.original.name === 'Manager Payable';
          const disabled =
            isEmbed ||
            row.original.type !== 'accounts' ||
            row.original.subRows.length > 0 ||
            !onOpenJournalEntries;

          const onClick = disabled
            ? undefined
            : () => {
                onOpenJournalEntries({
                  accountIds: accountId,
                  date: `${day(statementStartAt).yyyymmdd()}...${day(filter.endDate).yyyymmdd()}`,
                  party: isManager ? 'manager' : undefined,
                });
              };

          return (
            <Text
              ta="right"
              onClick={onClick}
              sx={
                !disabled
                  ? {
                      cursor: 'pointer',
                      transition: 'text-decoration 0.2s ease-in-out',
                      ':hover': {
                        textDecoration: 'underline',
                      },
                    }
                  : undefined
              }
            >
              {cell.getValue<string>()}
            </Text>
          );
        },
      },
    ],
    [currency, filter.endDate, isEmbed, onOpenJournalEntries, statementStartAt]
  );

  const table = useLazyTable({
    data: {
      error: queryData.error?.message,
      loading: queryData.isLoading,
      rows,
      rowCount: rows.length,
    },
    table: {
      columns,
      hideTopBar: true,
      hidePagination: true,
    },
    subRows: {
      getRowCanExpand: (row) => (row.subRows?.length ?? 0) > 0,
      getSubRows: (row) => row.subRows,
      defaultExpanded: true,
      allowExpandingAll: false,
    },
    styles: {
      row: (props, theme) => {
        const depth = 2 - props.row.depth;

        if (depth) {
          return {
            backgroundColor: `${theme.colors.neutral[depth]}!important`,
          };
        }

        return {};
      },
    },
  });

  const showBanner = useMemo(() => {
    const assets = rows[0]?.centTotal ?? 0;
    const liabilities = rows[1]?.centTotal ?? 0;
    return assets !== liabilities;
  }, [rows]);

  const action = useMemo<ComponentProps<typeof Banner>['action']>(() => {
    if (isEmbed || !onContactSupport) {
      return undefined;
    }

    return {
      label: 'Contact Support',
      onClick: onContactSupport,
      icon: 'CommentQuestionIcon',
    };
  }, [isEmbed, onContactSupport]);

  return (
    <>
      <Banner
        mounted={showBanner}
        type="info"
        title="Trust reconciliation is not balanced"
        message="Trust Assets do not equal Trust Liabilities. Please contact support for assistance."
        action={action}
      />
      <Group justify="space-between">
        <TrustReconcilationTableFilter />
        <Group>
          <Sort sorting={sorting} setSorting={setSorting} />
          <Divider orientation="vertical" />

          <Button
            variant="transparent"
            leftIcon="CollapseIcon"
            leftIconSize={14}
            onClick={() => table.toggleAllRowsExpanded()}
          >
            Collapse all
          </Button>
        </Group>
      </Group>
      <Divider mt="sm" />
      <MantineReactTable table={table} />
    </>
  );
};

const Sort = ({
  sorting,
  setSorting,
}: {
  sorting: TrustReconcilationTableQuerySorting | null;
  setSorting: (sorting: TrustReconcilationTableQuerySorting | null) => void;
}) => {
  const options = useMemo<SelectItem<TrustReconcilationTableQuerySorting>[]>(
    () => [
      {
        label: 'Name',
        value: 'name',
      },
      {
        label: 'Amount',
        value: 'centTotal',
      },
    ],
    []
  );

  const value = options.find((option) => option.value === sorting) ?? null;

  return (
    <Filter.Select
      label="Sort by"
      type="single"
      value={value}
      data={{ options }}
      setValue={(value) => setSorting(value?.value ?? null)}
    />
  );
};
